// SA-3
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { COL_SHIFTS } from '../../../config/collections'
import { PERMISSION_KEYS } from '../../../config/permissions'
import { formatShiftTimeRange, shiftActiveLabel } from '../../../lib/shifts'
import { matchesListSearch } from '../../../lib/listSearch'
import { pb } from '../../../pocketbase'
import { useAuth } from '../../../context/AuthContext'
import type { ShiftRecord } from '../../../types/records'
import { PageHeader, PageShell, Alert, ButtonLink, SearchField } from '../../../components/ui'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'
import {
  btn,
  cn,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableLastRowClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../../lib/ui'

export function ShiftsPage() {
  const { can } = useAuth()
  const [items, setItems] = useState<ShiftRecord[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const list = await pb.collection(COL_SHIFTS).getFullList<ShiftRecord>({ sort: 'sort,name' })
      setItems(list)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const displayedItems = useMemo(() => {
    if (!searchQuery.trim()) return items
    return items.filter((row) =>
      matchesListSearch(
        searchQuery,
        row.name,
        row.startTime,
        row.endTime,
        shiftActiveLabel(row.active),
        String(row.sort),
      ),
    )
  }, [items, searchQuery])

  return (
    <PageShell>
      <PageHeader
        title="Смены"
        lead="Графики 2/2 (Смена 1 и 2, обычно 08:00–20:00) и 5/2 (Смена 5/2, будни). Порядок отображения в фильтрах и графике."
      />
      {err && <Alert kind="err">{err}</Alert>}
      <div className={toolbarClass}>
        {can(PERMISSION_KEYS.SHIFTS_WRITE) && (
          <ButtonLink to="/admin/administration/shifts/new" variant="primary">
            Добавить смену
          </ButtonLink>
        )}
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
        <div className="flex min-w-40 flex-1 justify-end">
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Поиск по названию, времени…"
            aria-label="Поиск смен"
          />
        </div>
      </div>
      <div className={cn(tableWrapClass, tableLastRowClass)}>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={tableHeadClass}>Название</th>
              <th className={tableHeadClass}>Время</th>
              <th className={tableHeadClass}>Порядок</th>
              <th className={tableHeadClass}>Статус</th>
              <th className={tableHeadClass} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={5}>
                  <em>Загрузка…</em>
                </td>
              </tr>
            )}
            {!loading && displayedItems.length === 0 && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={5}>
                  <em>
                    {items.length === 0
                      ? 'Нет смен — добавьте первую или дождитесь применения миграции.'
                      : 'Ничего не найдено'}
                  </em>
                </td>
              </tr>
            )}
            {!loading &&
              displayedItems.map((row) => (
                <tr key={row.id} className={tableRowHoverClass}>
                  <td className={tableCellClass}>{row.name}</td>
                  <td className={tableCellClass}>{formatShiftTimeRange(row)}</td>
                  <td className={tableCellClass}>{row.sort}</td>
                  <td className={tableCellClass}>{shiftActiveLabel(row.active)}</td>
                  <td className={cn(tableCellClass, 'whitespace-nowrap')}>
                    {can(PERMISSION_KEYS.SHIFTS_WRITE) && (
                      <Link to={`/admin/administration/shifts/${row.id}`} className={btn('ghost')}>
                        Изменить
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  )
}
